<template>
  <va-modal
    :value="isOpen"
    id="createOrUpdatePatientModal"
    ref="createOrUpdatePatientModal"
    size="large"
    :noOutsideDismiss="true"
    :okText="$t('modals.buttons.save')"
    :cancelText="$t('modals.buttons.cancel')"
    @ok="submitForm"
    :okDisabled="isSubmitingForm || !canSubmitForm"
    @cancel="onClickCancel">

      <div slot="header" class="modal-title">
        {{
          isUpdating
            ? this.$t('clinical.people.update.title', { name: this.patient ? `${this.patient.firstName} ${this.patient.lastName}` : '' })
            : this.$t('clinical.people.create.title')
        }}
      </div>

    <div class="person-details evollu-form">
      <div class="row">
        <div class="col-12 col-md-6">
          <va-input
            type="text"
            v-model="form.firstName"
            :placeholder="$t('agenda.appointments.name')"
            :label="$t('agenda.appointments.name')"
            :maxlength="64"
            required
          />
        </div>
        <div class="col-12 col-md-6">
          <va-input
            type="text"
            v-model="form.lastName"
            :placeholder="$t('wellbeing.leads.table.last_name')"
            :label="$t('wellbeing.leads.table.last_name')"
            :maxlength="64"
            required
          />
        </div>

        <div class="col-12 col-md-6">
          <va-input
            type="email"
            v-model="form.email"
            :placeholder="$t('accounts.email')"
            :label="$t('accounts.email')"
            :maxlength="64"
            :error="formErrors.has('email') || invalidEmail"
            :error-messages="[$t(formErrors.get('email'))]"
            @input="formErrors.clear('email')"
          />
        </div>

        <div class="col-12 col-md-6">
          <label class="form-label">{{ $t("references.inputs.phone") }}</label>
          <vue-tel-input
            v-model="form.mobilePhone"
            class="w-100"
            maxlength="20"
            :preferredCountries="$store.state.countries.allowed"
            :disabledFetchingCountry="true"
            :defaultCountry="defaultCountrySelection"
            :placeholder="$t('references.inputs.phone')"
            @validate="mobilePhoneValidation">
          </vue-tel-input>
          <span class="small text-danger"
                v-show="form.mobilePhone !== '' && form.mobilePhone !== null && invalidMobilePhone">
                        {{ $t("patients.errors.invalidPhone") }}
                      </span>
        </div>

        <div class="col-12 col-md-6">
          <label class="form-label">{{ $t("people_manager.table.gender") }}</label>
          <multiselect
            v-model="form.gender"
            :options="genders"
            track-by="id"
            label="label"
            select-label=""
            selected-label=""
            deselect-label=""
            :placeholder="$t('people_manager.table.gender')"
            :allow-empty="false"
            :multiple="false"
            :searchable="false"
          />
        </div>

        <div class="col-12 col-md-6">
          <label class="form-label">{{ $t("tables.columns.birthdate") }}:</label>
          <DatePicker
            id="event-end"
            class="w-100"
            v-model="form.birthdate"
            :config="birthdatePickerConfig"
            :placeholder="$t('tables.columns.birthdate')"
          />
        </div>

        <div class="col-12 col-md-6">
          <va-input
            :label="$t('people_manager.tax_nr')"
            :placeholder="$t('people_manager.tax_nr')"
            v-model="form.taxNr"
            :error="formErrors.has('taxNr')"
            :error-messages="[$t(formErrors.get('taxNr'))]"
            @input="formErrors.clear('taxNr')"
            id="editing-nif"
            :maxlength="25"
            required/>
        </div>

        <div class="col-12 col-md-6">
          <va-input
            :label="$t('people_manager.health_nr')"
            :placeholder="$t('people_manager.health_nr')"
            v-model="form.healthNr"
            id="editing-health-nr"
            :error="formErrors.has('healthNr')"
            :error-messages="[$t(formErrors.get('healthNr'))]"
            @input="formErrors.clear('healthNr')"
            :maxlength="25"
            required/>
        </div>

      </div>
    </div>

  </va-modal>
</template>

<script>
import { EMAIL_REGEX } from '@/utils/regex';
import Errors from '@/utils/StandardErrors';
import DatePicker from '@/components/common/DatePicker.vue';
import moment from 'moment';
import { VueTelInput } from "vue-tel-input";
import Multiselect from 'vue-multiselect'
import { PeopleManagerService } from '@/api/PeopleManagerService';

export default {
  components: { DatePicker, VueTelInput, Multiselect },
  props: {
    service: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    },
    patient: {
      type: Object,
      required: false
    }
  },
  data: function() {
    return {
      genders: [
        { id: null, label: "N/A" },
        { id: 'female', label: this.$t('gender.female') },
        { id: 'male', label: this.$t('gender.male') },
      ],
      isOpen: false,
      isSubmitingForm: false,
      vueTelInputRedrawIndex: 0,
      invalidMobilePhone: false,
      form: {
        firstName: null,
        lastName: null,
        birthdate: null,
        email: null,
        mobilePhone: null,
        gender: null,
        taxNr: null,
        healthNr: null
      },
      formErrors: new Errors(),
      birthdatePickerConfig: {
        enableTime: false,
        maxDate: moment().toDate(),
        noCalendar: false,
        dateFormat: "Y-m-d"
      },
    }
  },
  mounted: function () {
    this.isOpen = true
    this.prefillForm()
  },
  methods: {
    prefillForm: function () {
      if(this.patient) {
        let formData = JSON.parse(JSON.stringify(this.patient))
        if (this.patient.gender) {
          formData.gender = this.genders.find(gender => gender.id === this.patient.gender)
        }else {
          formData.gender = this.genders[0]
        }
        if (this.patient.birthdate !== null && this.patient.birthdate !== undefined && this.patient.birthdate !== '' && this.patient.birthdate !== '0000-00-00 00:00:00') {
          formData.birthdate = moment(this.patient.birthdate).format('YYYY-MM-DD')
        }
        this.form = {...formData}
      }
    },
    submitForm: async function() {
      if (this.canSubmitForm) {
        this.isSubmitingForm = true
        try {
          const formData = { ...this.form }
          delete formData.createdAt
          delete formData.originEntityId
          delete formData.originFacilityId
          formData.gender = formData.gender.id
          const updatedPatient = await PeopleManagerService.updatePerson(parseInt(this.patient.id), formData)
          this.showToast(this.$t('people_manager.messages.patient_updated'), this.$store.state.successToastOptions)
          this.isSubmitingForm = false
          this.$emit('patientUpdated', updatedPatient)
          setTimeout(() => {
            this.isOpen = false
          }, 500)
        } catch (e) {
          console.log(e)
          this.showToast(this.$t('people_manager.errors.error_updating_person'), this.$store.state.errorToastOptions)
          this.isSubmitingForm = false
        }
      }
    },
    onClickCancel: function () {
      this.$emit('cancel')
      setTimeout(() => {
        this.isOpen = false
      }, 500)
    },
    mobilePhoneValidation: function (data) {
      this.invalidMobilePhone = !data.isValid
      if (data.isValid) {
        this.form.mobilePhone = data.number.international
      }
    }
  },
  computed: {
    isUpdating: function () {
      return this.patient !== null && this.patient !== undefined
    },
    invalidEmail: function () {
      if (this.form.email ) {
        if(!this.form.email.match(EMAIL_REGEX)) {
          this.formErrors.set('email', 'people.errors.email_must_be_a_valid_email')
          return true
        }
      }
      return false
    },
    authUserEntity: function() {
      return this.$auth.user().entity;
    },
    defaultCountrySelection: function() {
      if (this.authUserEntity && this.authUserEntity.country) {
        return this.authUserEntity.country;
      }
      return this.$store.state.countries.default;
    },
    canSubmitForm: function () {
      if(this.form.firstName === null
        || (this.form.lastName === null || this.form.lastName === '')
        || (this.form.birthdate === null || this.form.birthdate === '')
        || (this.form.email === null || this.form.email === '')
        || ((this.form.taxNr === null || this.form.taxNr === '') && (this.form.healthNr === null || this.form.healthNr === ''))) {
        return false
      }
      if(this.form.email && this.invalidEmail) {
        return false
      }
      if(this.form.mobilePhone && this.invalidMobilePhone) {
        return false
      }
      return !this.formErrors.any();
    }
  }
};
</script>

<style scoped lang="scss">

</style>
